import React, { FC, Children, useCallback } from 'react';
import styled from 'styled-components';
import { Direction } from 'reactstrap/lib/Dropdown';
import { useTranslation } from '@blocs.i18n';
import Dropdown from '@minecraft.dropdown';
import DropdownMenu from '@minecraft.dropdown-menu';
import DropdownToggle from '@minecraft.dropdown-toggle';
import { CoreTheme as theme } from '@minecraft.themes';
import { useThemeSwitcherContext } from '@minecraft.theme-switcher';
import Icon, { IconProps } from '@minecraft.icon';
import { useModal } from '@minecraft.modal';

export const TOGGLE_BUTTON_TEST_ID = 'TOGGLE_BUTTON_TEST_ID';

const StyledDropdownMenu = styled(DropdownMenu)`
  background-color: ${(props): string => props.theme.meatballMenu.backgroundColor};
  border-color: ${(props): string => props.theme.meatballMenu.borderColor};
  color: ${(props): string => props.theme.meatballMenu.color};

  .dropdown-item {
    &,
    &:focus,
    &:active,
    &:hover,
    &:visited,
    &:not([href]):not([tabindex]),
    &:not([href]):not([tabindex]):hover {
      color: ${(props): string => props.theme.meatballMenu.color};
      font-weight: inherit;
      text-decoration: none;
    }
  }

  .dropdown-item:hover {
    background-color: ${(props): string => props.theme.meatballMenu.item.state.hover.backgroundColor};
  }

  z-index: 1051;
`;

export type Props = {
  children?: React.ReactNode;
  className?: string;
  direction?: Direction;
  color?: string;
  disabled?: boolean;
  qaId?: string;
  // @deprecated use data-testid instead
  testId?: string;
  'data-testid'?: string;
  shouldCloseOnItemClick?: boolean;
  toggleIconProps?: Partial<Omit<IconProps, 'name' | 'color'>>;
};

type GetIconColorArgs = {
  isDarkTheme?: boolean;
  disabled?: boolean;
  color?: string;
};

export const getIconColor = ({ isDarkTheme, disabled, color }: GetIconColorArgs): string => {
  if (disabled) {
    return theme.colors.primary.ltGrey1;
  }

  if (color) {
    return color;
  }

  return isDarkTheme ? theme.colors.primary.ltGrey2 : theme.colors.primary.darkGrey;
};

const MeatballMenu: FC<Props> = ({
  children,
  className,
  direction = 'left',
  color,
  disabled = false,
  qaId,
  testId,
  'data-testid': dataTestId,
  shouldCloseOnItemClick = false,
  toggleIconProps = {},
}) => {
  const { t } = useTranslation();
  const { isDarkTheme } = useThemeSwitcherContext();
  const { isOpen, toggleModal: toggleDropdown } = useModal();
  const childElements = Children.toArray(children);

  const toggle = useCallback(
    (e) => {
      e.stopPropagation();
      // TODO Put this back but it'll cause a few issues across the board
      // This SHOULD prevent default and it does fix the issue:
      // https://castingnetworks.atlassian.net/browse/MAT-201
      // It's worth a full regression test when we have time to do so.
      // Known bugs that were resolved when this was enabled, follow the pattern of these fixes
      // across all uses of the Meatball menu:
      // https://castingnetworks.atlassian.net/browse/CNEE-686
      // https://castingnetworks.atlassian.net/browse/CNEE-692
      // https://castingnetworks.atlassian.net/browse/CNEE-694
      // e.preventDefault();
      toggleDropdown();
    },
    [toggleDropdown]
  );

  return (
    <>
      {childElements.length > 0 &&
        (disabled ? (
          <Icon
            {...toggleIconProps}
            className={className}
            name="menu-meatball"
            data-disabled={disabled}
            color={getIconColor({ disabled })}
          />
        ) : (
          <Dropdown direction={direction} isOpen={isOpen} toggle={toggle}>
            <DropdownToggle
              aria-label={t('common:label.openMenu')}
              data-toggle="dropdown"
              data-testid={TOGGLE_BUTTON_TEST_ID}
              data-qa-id={qaId}
              style={{
                backgroundColor: 'transparent',
                textDecoration: 'none',
                margin: 0,
                padding: 0,
                border: 'none',
              }}
            >
              <Icon
                {...toggleIconProps}
                className={className}
                name="menu-meatball"
                color={getIconColor({ isDarkTheme, color })}
              />
            </DropdownToggle>
            <StyledDropdownMenu
              // This onClick allows for the meatball menu to be closed when clicking on the dropdown items
              // This is needed in order to to support the opening of a modal when clicking on a dropdown item
              onClick={shouldCloseOnItemClick ? toggle : undefined}
              container="body"
              data-testid={dataTestId ?? testId}
              data-qa-id="meatballMenu-dropdown"
            >
              {children}
            </StyledDropdownMenu>
          </Dropdown>
        ))}
    </>
  );
};

export default MeatballMenu;
